import { MeasurementSystemEnum } from '@inter-ikea-kompis/enums';
import { WeightUtility } from '@inter-ikea-kompis/utilities';
class ConfigurationSummaryWeightUtility {
    /**
     * @returns Total package weight in kilos.
     * @param shoppingProducts ...
     * @param includedProducts ...
     */
    static getTotalWeightInKg(shoppingProducts, includedProducts) {
        const includedShoppingProducts = this.getIncludedShoppingProducts(shoppingProducts, includedProducts);
        return [...shoppingProducts, ...includedShoppingProducts]
            .map((sp) => (sp.product.content?.packageMeasure?.totalWeightGross || 0) * sp.quantity)
            .reduce((prev, curr) => prev + curr, 0);
    }
    /**
     * @param kilos The value in kilos.
     * @param locale Locale in format xx-XX, e.g. En-GB.
     * @param translations Phrase translations.
     * @param useMetricMeasures LocalizedInformation or dexf settings key.
     * @param measurementSystem Override market setting "useMetricMeasures" by controlling the measurement system in the planner.
     * @returns A formatted and localized weight with the correct unit (kg or lb and oz).
     */
    static getLocalizedWeight(kilos, locale, translations, useMetricMeasures, measurementSystem = null) {
        if (measurementSystem === MeasurementSystemEnum.metric) {
            return this.getMetricWeight(kilos, locale, translations);
        }
        else if (measurementSystem === MeasurementSystemEnum.imperial) {
            return this.getImperialWeight(kilos, translations);
        }
        else {
            if (useMetricMeasures) {
                return this.getMetricWeight(kilos, locale, translations);
            }
        }
        return this.getImperialWeight(kilos, translations);
    }
    /**
     * @param localizedWeight Localized weight object returned from ConfigurationSummaryWeightUtility.getLocalizedWeight.
     * @returns Formatted string with HTML entities such as &nbsp;.
     */
    static getLocalizedWeightHtmlString(localizedWeight) {
        const nbsp = '\u00A0';
        if ('kgValue' in localizedWeight) {
            const { kgValue, kgUnit } = localizedWeight;
            return `${kgValue}${nbsp}${kgUnit}`;
        }
        else if ('lbValue' in localizedWeight) {
            const { lbValue, lbUnit, ozValue, ozUnit } = localizedWeight;
            return `${lbValue}${nbsp}${lbUnit} ${ozValue}${nbsp}${ozUnit}`;
        }
        else {
            const { ozValue, ozUnit } = localizedWeight;
            return `${ozValue}${nbsp}${ozUnit}`;
        }
    }
    /**
     * @returns Included shopping items based on SPR products and includedProducts.
     * @param shoppingProducts ...
     * @param includedProducts ...
     */
    static getIncludedShoppingProducts(shoppingProducts, includedProducts) {
        const sprChildren = [];
        for (const shoppingProduct of shoppingProducts) {
            const sprQuantity = shoppingProduct.quantity;
            const children = shoppingProduct.product.content?.child?.map(({ itemId, quantity, sortNo }) => ({
                itemId,
                quantity: quantity * sprQuantity,
                sortNo
            })) || [];
            sprChildren.push(...children);
        }
        return sprChildren.map((sprChild) => {
            const matchingIncludedProduct = includedProducts?.find(({ itemId }) => itemId === sprChild.itemId) || null;
            if (!matchingIncludedProduct) {
                throw Error(`Could not find matching included product within SPR for itemId "${sprChild.itemId}".`);
            }
            return {
                quantity: sprChild.quantity,
                product: matchingIncludedProduct
            };
        });
    }
}
ConfigurationSummaryWeightUtility.getMetricWeight = (kilos, locale, translations) => {
    // Multiplying and dividing to round the fractions.
    const roundedKilos = Math.round(kilos * 100) / 100;
    const weightUnitKg = translations.weightUnitKg;
    const formattedKilos = roundedKilos.toLocaleString(locale, {
        minimumFractionDigits: 2
    });
    return { kgValue: formattedKilos, kgUnit: weightUnitKg };
};
ConfigurationSummaryWeightUtility.getImperialWeight = (kilos, translations) => {
    const { weightValuePound, weightValueOunce } = translations;
    const { lb, oz } = WeightUtility.metricToImperial(kilos);
    return {
        ...(lb ? { lbValue: `${lb}`, lbUnit: weightValuePound } : null),
        ozValue: `${oz}`,
        ozUnit: weightValueOunce
    };
};
export default ConfigurationSummaryWeightUtility;
//# sourceMappingURL=ConfigurationSummaryWeightUtility.js.map